import {
  ATHENA_CONFIG_VERSION,
  AthenaReferringProviderMatchStrategy,
  type AthenaReferralSource,
  type AthenaTenantConfig,
  type AthenaTenantConfigOverrides,
} from "@/configuration/ehr/athena/types";
import { requireCatalogPayerCodes } from "@/src/lib/ehr/athena/athena-payer-codebook";
import { Tenancy } from "@/src/lib/tenancy";
import { AppointmentType } from "@/src/models/appointment";
import { getPractitionerNameWithCredentials, type Practitioner } from "@/src/models/practitioner";

export const ATHENA_DOCUMENT_PRIORITY = {
  NORMAL: "2",
  URGENT: "1",
} as const;

export const ATHENA_CARE_TEAM_RECIPIENT_CLASS_IDS = {
  INSURANCE_ADJUSTER: "1",
  PRIMARY_CARE_PROVIDER: "3",
  REFERRING_PROVIDER: "4",
} as const;

export const ATHENA_REFERRAL_DOCUMENT_TYPE_IDS = {
  ESTABLISHED_PATIENT: 410549,
  NEW_PATIENT: 391737,
} as const;

export const ATHENA_REFERRAL_SOURCE_IDS = {
  PCP: "2",
  SPECIALIST: "41",
} as const;

export const DEFAULT_ATHENA_CASE_SUBJECT = "Patient Referral";

const PCP_SPECIALTIES = [
  "Internal Medicine",
  "Family Medicine",
  "Family Practice",
  "General Practice",
  "Primary Care",
  "Nurse Practitioner",
  "Family Nurse Practitioner",
  "General Internal Medicine",
  "Family and General Practice",
];

export function isPrimaryCareSpecialty(specialty: string | null | undefined): boolean {
  if (!specialty) return false;

  const normalizedSpecialty = specialty.toLowerCase().trim();

  return PCP_SPECIALTIES.some((candidate) => normalizedSpecialty.includes(candidate.toLowerCase()));
}

export function resolveDefaultReferralSource(
  referringProvider: Practitioner | null | undefined,
  isExistingPatient: boolean
): AthenaReferralSource | null {
  if (isExistingPatient || !referringProvider) return null;

  return {
    referralsourceid:
      referringProvider.specialty && !isPrimaryCareSpecialty(referringProvider.specialty)
        ? ATHENA_REFERRAL_SOURCE_IDS.SPECIALIST
        : ATHENA_REFERRAL_SOURCE_IDS.PCP,
    referralsourceother: getPractitionerNameWithCredentials(referringProvider),
  };
}

const DEFAULT_APPOINTMENT_PATIENT_CASE_POLICY = {
  allowNewWithUpcoming: false,
};

export function defineAthenaTenantConfig(overrides: AthenaTenantConfigOverrides): AthenaTenantConfig {
  const patientCaseProviderAssignment = overrides.patientCases?.providerAssignment ?? (() => null);
  const shouldCreatePatientCase =
    overrides.patientCases?.shouldCreate ?? ((params) => patientCaseProviderAssignment(params) !== null);
  const insurance = {
    patientCoverageDepartmentId: null,
    mapping: null,
    payerCodes: {},
    resolveCasePolicy: () => null,
    strictPlanMatching: false,
    ...overrides.insurance,
  };

  requireCatalogPayerCodes(insurance.payerCodes);

  return {
    version: ATHENA_CONFIG_VERSION,
    subdomain: overrides.subdomain,
    core: {
      defaultReceivingLocationEhrId: null,
      ...overrides.core,
    },
    patient: {
      consent: null,
      referralSource: resolveDefaultReferralSource,
      referringProviderMatchStrategy: AthenaReferringProviderMatchStrategy.LEGACY,
      updateInsurance: true,
      updatePatient: true,
      createReferringProvider: true,
      overridePhoneNumbers: false,
      createPatientWithoutRequiredDemographics: false,
      ...overrides.patient,
    },
    insurance,
    documents: {
      lookbackDays: 7,
      referralDocumentTypeId: () => null,
      note: () => "Reviewed by Valerie Health",
      priority: () => ATHENA_DOCUMENT_PRIORITY.NORMAL,
      ...overrides.documents,
    },
    patientCases: {
      createWithUrgency: () => false,
      caseDepartment: (registrationDepartmentId) => registrationDepartmentId,
      providerAssignment: patientCaseProviderAssignment,
      shouldCreate: shouldCreatePatientCase,
      assignee: () => null,
      note: () => "",
      subject: () => DEFAULT_ATHENA_CASE_SUBJECT,
      source: "OTHER",
      documentSubclass: "REFERRAL",
      outboundOnly: true,
      lookbackDays: 30,
      fetchExistingAppointmentForSubject: false,
      persistRoutedLocation: false,
      ...overrides.patientCases,
    },
    scheduling: {
      suppressAutomaticPatientCasesForVoice: false,
      additionalAppointmentConfirmationDepartmentIds: [],
      additionalAppointmentHistoryDepartmentIds: [],
      procedureContinuityHistoryDepartmentIds: [],
      createTicklerWithUrgency: () => false,
      ticklerProviderAssignment: (providerId) => providerId,
      reminderNote: () => "",
      resolveAppointmentTypeId: ({ group, appointmentType }) => {
        if (group.tenancy !== Tenancy.LIVE) return 4;

        const isNew =
          appointmentType === AppointmentType.INITIAL_CONSULTATION ||
          appointmentType === AppointmentType.NEW_PATIENT_CHECKIN;

        return isNew ? 1121 : 241;
      },
      appointmentTypes: [],
      appointmentPatientCasePolicy: DEFAULT_APPOINTMENT_PATIENT_CASE_POLICY,
      eligibilityHistoryDepartmentIds: null,
      bookedAppointmentType: () => null,
      bookedTypeIdFromOpenTypeId: () => null,
      preserveBookedTypeForRescheduling: false,
      acceptBackingOpenAppointmentTypes: false,
      blockingOrders: null,
      sendVoiceCancellationReason: false,
      ...overrides.scheduling,
    },
  };
}
